import { useSelector } from '@/api/state';
import { usePermissions, useUserHasPermission } from '@/hooks/usePermissions';
import ConfirmDeleteCustomerReminderModal from '@/ui/components/servicePlanCustomerReminderControl/ConfirmDeleteCustomerReminderModal';
import {
  AnchorButton,
  ManageableItemsList,
  Tooltip,
  TTableRowAction,
  useToastContext,
} from '@payaca/ambrosia';

import SwitchField from '@payaca/components/switchField/SwitchField';
import { ServicePlanPermissions } from '@payaca/permissions/service-plans/service-plans.permissions';
import * as servicePlansActions from '@payaca/store/servicePlans/servicePlansActions';
import {
  getServicePlansCustomerReminders,
  toggleServicePlansCustomerReminder,
} from '@payaca/store/servicePlans/servicePlansActions';
import { PublicHydratedServicePlanCustomerReminderTemplate } from '@payaca/types/service-plans';
import moment from 'moment-timezone';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

const ServicePlansCustomerRemindersListed: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pushInfo } = useToastContext();
  const { userHasRequiredPermission } = usePermissions();
  const [showDeleteCustomerReminderModal, setShowDeleteCustomerReminderModal] =
    useState<
      false | PublicHydratedServicePlanCustomerReminderTemplate['publicId']
    >(false);
  const [isDeletingCustomerReminder, setIsDeletingCustomerReminder] =
    useState(false);
  const [
    togglingCustomerReminderPublicId,
    setTogglingCustomerReminderPublicId,
  ] = useState<string | undefined>();

  const isGettingListedServicePlanCustomerReminders = useSelector(
    (state) => state.servicePlans.isGettingListedServicePlanCustomerReminders
  );
  const listedCustomerReminders = useSelector(
    (state) => state.servicePlans.listedServicePlanCustomerReminders
  );

  useEffect(() => {
    dispatch(getServicePlansCustomerReminders.request({}));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const toggleCustomerReminder = (publicId: string, isEnabled: boolean) => {
    if (togglingCustomerReminderPublicId) return;

    setTogglingCustomerReminderPublicId(publicId);

    dispatch(
      toggleServicePlansCustomerReminder.request({
        publicId,
        body: {
          isEnabled,
        },
        onSuccess: () => setTogglingCustomerReminderPublicId(undefined),
        onError: () => {
          pushInfo(
            'Failed to toggle customer reminder, try again later or contact support.'
          );
          setTogglingCustomerReminderPublicId(undefined);
        },
      })
    );
  };

  const tableQuickActions = useMemo(() => {
    const actions: TTableRowAction<PublicHydratedServicePlanCustomerReminderTemplate>[] =
      [];

    if (
      userHasRequiredPermission([ServicePlanPermissions.PERSIST_SERVICE_PLAN])
    ) {
      actions.push({
        label: 'Edit',
        onClick: (row) =>
          history.push(`/service-plans/customer-reminders/${row.publicId}`),
      });

      actions.push({
        label: 'Delete',
        onClick: (row) => {
          setShowDeleteCustomerReminderModal(row.publicId);
        },
      });
    }

    return actions;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'history'
  }, [userHasRequiredPermission]);

  const hasPermissionToCreateReminder = useUserHasPermission({
    permissions: [ServicePlanPermissions.PERSIST_SERVICE_PLAN],
  });

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Customer reminders"
          subHeading="Set up reminders to let your customers know when their service plans are ready for renewal"
          buttons={
            <Tooltip
              tooltipContent="You don't have permission to perform this action"
              enabled={!hasPermissionToCreateReminder}
            >
              <AnchorButton
                as={Link}
                to="/service-plans/customer-reminders/new"
                size="sm"
                disabled={!hasPermissionToCreateReminder}
              >
                Create customer reminder
              </AnchorButton>
            </Tooltip>
          }
        />

        <ManageableItemsList.Table
          uniqueKey="publicId"
          items={listedCustomerReminders}
          isLoading={isGettingListedServicePlanCustomerReminders}
          itemActions={tableQuickActions}
        >
          <ManageableItemsList.Table.Column header="Name" field="name" />
          <ManageableItemsList.Table.Column<
            PublicHydratedServicePlanCustomerReminderTemplate,
            'channels'
          >
            header="Type"
            field="channels"
            render={(value) =>
              value
                .map((i) => i.charAt(0).toUpperCase() + i.slice(1))
                .join(', ')
            }
          />
          <ManageableItemsList.Table.Column<
            PublicHydratedServicePlanCustomerReminderTemplate,
            'timeOffset'
          >
            header="Trigger"
            field="timeOffset"
            render={(value) =>
              Math.abs(moment.duration(value).asDays()) + ' days before renewal'
            }
          />
          <ManageableItemsList.Table.Column<
            PublicHydratedServicePlanCustomerReminderTemplate,
            'isEnabled'
          >
            header="State"
            field="isEnabled"
            render={(value, row) => (
              <SwitchField
                isDisabled={row.publicId === togglingCustomerReminderPublicId}
                name="isEnabled"
                label="Off/On"
                value={value}
                onChange={(e) =>
                  toggleCustomerReminder(row.publicId, e.isEnabled)
                }
              />
            )}
          />
        </ManageableItemsList.Table>
      </ManageableItemsList>

      <ConfirmDeleteCustomerReminderModal
        isOpen={!!showDeleteCustomerReminderModal}
        isBusy={isDeletingCustomerReminder}
        onClose={() => setShowDeleteCustomerReminderModal(false)}
        onConfirmDelete={() => {
          if (!showDeleteCustomerReminderModal) return;

          setIsDeletingCustomerReminder(true);

          dispatch(
            servicePlansActions.deleteServicePlansCustomerReminder.request({
              publicId: showDeleteCustomerReminderModal,
              onSuccess: () => {
                dispatch(
                  servicePlansActions.getServicePlansCustomerReminders.request(
                    {}
                  )
                );
                setIsDeletingCustomerReminder(false);
                setShowDeleteCustomerReminderModal(false);
              },
              onError: () => {
                pushInfo(
                  'Failed to delete customer reminder, try again later or contact support.'
                );
                setIsDeletingCustomerReminder(false);
                setShowDeleteCustomerReminderModal(false);
              },
            })
          );
        }}
      />
    </>
  );
};

export default ServicePlansCustomerRemindersListed;
